import { useState, useRef, useEffect, useCallback } from 'react';
import useCustomStore from '../../store/customStore';
import useAuthStore from '../../store/authStore';
import {
    Loader2, Sparkles, AlertTriangle, CheckCircle, PenLine, Brain, FileJson, Layers, Settings, ArrowRight, Lock
} from 'lucide-react';
import TrialStartCard from '../../components/custom/TrialStartCard';
import Illustration from '../../components/ui/Illustration';
import illuAjouterContenu from '../../assets/illustrations/fonctionnalites/ajouter-contenu.svg?raw';
import illuHistorique from '../../assets/illustrations/fonctionnalites/historique.svg?raw';
import illuGestionCodes from '../../assets/illustrations/fonctionnalites/gestion-codes.svg?raw';
import illuTeleverser from '../../assets/illustrations/fonctionnalites/televerser.svg?raw';
import TrialExamplesPicker from '../../components/custom/TrialExamplesPicker';
import PromptIdeas from '../../components/custom/PromptIdeas';
import api from '../../services/api';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useAiQuota from '../../hooks/useAiQuota';
import PackBoosterCTA from '../../components/custom/PackBoosterCTA';
import { extractTextFromFile, estimateTokens } from '../../utils/extractDocText';
import PdfPreviewModal from './PdfPreviewModal';
import MaThematiqueGenerations from './MaThematiqueGenerations';
import CodesTab from './CodesTab';
import DocumentModeFields from './upload/DocumentModeFields';
import MultiDocPicker from './upload/MultiDocPicker';
import RecentGenerations from './upload/RecentGenerations';
import SourceSelector from './upload/SourceSelector';
import EnrichmentSwitch from './upload/EnrichmentSwitch';
import MultiDocList from './upload/MultiDocList';
import AiBatchSection from './upload/AiBatchSection';
import JsonItemsSection from './upload/JsonItemsSection';
import AiContentNotice from '../../components/custom/AiContentNotice';

const MAX_TOKENS_TOTAL = 150000;
const MAX_DOCS = 10;
const MAX_SIZE_PER_DOC = 10 * 1024 * 1024;

/** Extract a clean label from a document filename */
function labelFromFilename(name) {
    return name
        .replace(/\.(pdf|docx|txt|csv|html|jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, c => c.toUpperCase()); // Capitalize first letter
}

/**
 * @param {object|null} enrichmentTargetProp — Si fourni { id, label }, le composant passe
 *   en mode "compléter sujet existant". Sinon, on lit le store (enrichmentTarget posé
 *   par MaThematiqueGenerations via le bouton "+ Compléter ce sujet").
 */

export default function MaThematiqueUpload({ scopeId, thematiqueId, onGenerated, onOpenSujet = null, initialCategoryId = null, trial = null, onUnlock = null, enrichmentTarget: enrichmentTargetProp = null, forceExpanded = false }) {
    const {
        sousSujets, categories, quota, uploading, activeJob,
        fetchList, uploadPdf, stopPolling, resubmitJob, startPolling, clearActiveJob,
        enrichmentTarget: enrichmentTargetStore, setEnrichmentTarget, clearEnrichmentTarget,
        promptPrefill, clearPromptPrefill,
    } = useCustomStore();
    // Source de vérité : prop d'abord (si parent veut forcer), sinon store
    const enrichmentTarget = enrichmentTargetProp || enrichmentTargetStore;
    const user = useAuthStore(s => s.user);
    const isAdmin = user?.role === 'admin';
    const aiQuota = useAiQuota();

    const [label, setLabel] = useState('');
    const [file, setFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [categoryId, setCategoryId] = useState('');
    // Ouverture depuis la roue d'une catégorie : catégorie présélectionnée (0 = sans catégorie).
    useEffect(() => {
        if (initialCategoryId !== null && initialCategoryId !== undefined) setCategoryId(initialCategoryId > 0 ? String(initialCategoryId) : '');
    }, [initialCategoryId]);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Etat ouvert/fermé + tab active, persistés en localStorage par thematique
    // pour survivre aux changements de tab dans ScopeLanding et aux reloads.
    const [expandedLocal, setExpanded] = useState(() => {
        if (typeof window === 'undefined' || !thematiqueId) return false;
        try {
            const raw = window.localStorage.getItem(`mt-upload-state-${thematiqueId}`);
            return raw ? !!JSON.parse(raw).expanded : false;
        } catch { return false; }
    });
    // En mode modale (forceExpanded), on saute l'état collapsed : le bouton
    // "Gestion des sujets" trigger devient redondant avec le titre de la modale.
    const expanded = forceExpanded || expandedLocal;
    const [activeTab, setActiveTab] = useState(() => {
        if (typeof window === 'undefined' || !thematiqueId) return 'add';
        try {
            const raw = window.localStorage.getItem(`mt-upload-state-${thematiqueId}`);
            const t = raw ? JSON.parse(raw).activeTab : null;
            return (t === 'add' || t === 'history') ? t : 'add';
        } catch { return 'add'; }
    });
    const [createMode, setCreateMode] = useState('document'); // 'document' | 'empty' | 'ai-prompt' | 'multi-doc'
    const [dragging, setDragging] = useState(false);

    // Si la prop enrichmentTarget est fournie (mode "Compléter sujet existant"),
    // ouvre auto la modale + force le mode document (ou multi-doc si user switch ensuite).
    useEffect(() => {
        if (enrichmentTarget) {
            setExpanded(true);
            if (createMode !== 'document' && createMode !== 'multi-doc') {
                setCreateMode('document');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enrichmentTarget?.id]);

    // Multi-doc compilation mode : liste de docs extraits côté client
    const [multiDocs, setMultiDocs] = useState([]); // [{ file, name, size, mime, text, tokens, extracting?: bool, error?: string }]
    const [multiDocSubmitting, setMultiDocSubmitting] = useState(false);
    const multiDocFileRef = useRef(null);

    // AI prompt mode
    const [aiConsignes, setAiConsignes] = useState('');
    const [aiNbQuestions, setAiNbQuestions] = useState(20);
    const [aiGenerating, setAiGenerating] = useState(false);
    // Admin batch generation : sujets additionnels (titre + consignes)
    const [aiBatchExtra, setAiBatchExtra] = useState([]); // [{ label, consignes }]
    const [aiBatchProgress, setAiBatchProgress] = useState(null); // { current, total, success, errors }

    // Empty subject creation
    const [creatingEmpty, setCreatingEmpty] = useState(false);

    // JSON import mode — multi-fichiers supporté
    const [jsonItems, setJsonItems] = useState([]); // [{ file, data, preview }]
    const [jsonImporting, setJsonImporting] = useState(false);
    const [jsonImportProgress, setJsonImportProgress] = useState(null); // { current, total, results: [{filename, ok, nbS, nbQ, error?}] }

    const [recentOpen, setRecentOpen] = useState(false); // « Générations récentes » repliable (fermé par défaut)

    // Popup d'échec de génération : au 1er échec d'une génération en cours, on
    // propose de resoumettre en direct (le service est peut-être saturé).
    const [failedJob, setFailedJob] = useState(null);
    const failedJobShownRef = useRef(null);
    useEffect(() => {
        if (activeJob?.status === 'FAILED' && activeJob.job_id !== failedJobShownRef.current) {
            failedJobShownRef.current = activeJob.job_id;
            setFailedJob(activeJob);
        }
    }, [activeJob]);

    // "Modifier & régénérer" depuis l'historique : pré-remplit le formulaire
    // Génération IA avec le prompt du sujet choisi (label + consignes) et bascule dessus.
    useEffect(() => {
        if (!promptPrefill) return;
        setExpanded(true);
        setActiveTab('add');
        setCreateMode('ai-prompt');
        setLabel(promptPrefill.label || '');
        setAiConsignes(promptPrefill.consignes || '');
        setError('');
        clearPromptPrefill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promptPrefill]);

    // === Mode multi-doc : extraction client + upload aggregé ===
    const totalMultiTokens = multiDocs.reduce((sum, d) => sum + (d.tokens || 0), 0);

    const handleAddMultiDocFiles = async (filesList) => {
        const files = Array.from(filesList || []);
        if (files.length === 0) return;

        // Pour chaque file : valide taille, ajoute en "extracting", lance l'extraction
        const newEntries = [];
        for (const file of files) {
            if (multiDocs.length + newEntries.length >= MAX_DOCS) {
                setError(`Maximum ${MAX_DOCS} documents par compilation.`);
                break;
            }
            if (file.size > MAX_SIZE_PER_DOC) {
                setError(`"${file.name}" dépasse 10 Mo.`);
                continue;
            }
            newEntries.push({
                file,
                name: file.name,
                size: file.size,
                mime: file.type,
                text: '',
                tokens: 0,
                extracting: true,
                error: null,
            });
        }
        if (newEntries.length === 0) return;
        setMultiDocs(prev => [...prev, ...newEntries]);
        setError('');

        // Extraction séquentielle (évite saturation CPU sur les gros PDF)
        for (let i = 0; i < newEntries.length; i++) {
            const entry = newEntries[i];
            try {
                const { text } = await extractTextFromFile(entry.file);
                const tokens = estimateTokens(text);
                setMultiDocs(prev => {
                    // Check si en ajoutant celui-ci on dépasse → refus
                    const others = prev.filter(d => d.file !== entry.file);
                    const otherTokens = others.reduce((s, d) => s + (d.tokens || 0), 0);
                    if (otherTokens + tokens > MAX_TOKENS_TOTAL) {
                        setError(`"${entry.name}" ferait dépasser la capacité de ${MAX_TOKENS_TOTAL.toLocaleString()} tokens. Document non ajouté.`);
                        return others;
                    }
                    return prev.map(d => d.file === entry.file
                        ? { ...d, text, tokens, extracting: false }
                        : d);
                });
            } catch (e) {
                setMultiDocs(prev => prev.map(d => d.file === entry.file
                    ? { ...d, extracting: false, error: e.message || 'Erreur extraction' }
                    : d));
            }
        }
    };

    const removeMultiDoc = (index) => {
        setMultiDocs(prev => prev.filter((_, i) => i !== index));
        setError('');
    };

    const handleSubmitMultiDoc = async (e) => {
        e?.preventDefault();
        // En mode enrichissement, label/category sont récupérés du parent côté backend
        if (!enrichmentTarget && !label.trim()) return setError('Donnez un nom à la compilation');
        const ready = multiDocs.filter(d => !d.extracting && !d.error && d.text);
        if (ready.length === 0) return setError('Aucun document prêt');
        if (totalMultiTokens > MAX_TOKENS_TOTAL) return setError('Capacité dépassée');

        setMultiDocSubmitting(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('thematique_id', String(thematiqueId));
            formData.append('nb_questions', String(aiNbQuestions));
            if (enrichmentTarget) {
                formData.append('target_parent_ucss_id', String(enrichmentTarget.id));
            } else {
                formData.append('label', label.trim());
                if (categoryId) formData.append('user_category_id', String(categoryId));
            }
            ready.forEach((doc, i) => {
                formData.append(`documents[${i}]`, doc.file, doc.name);
                formData.append(`documents_text[${i}]`, doc.text);
            });
            const { data } = await api.post('/custom/upload-multi.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (data.success) {
                setSuccessMsg(`Compilation lancée : ${data.data.nb_documents} docs · ${data.data.total_tokens.toLocaleString()} tokens. Le sujet sera prêt dans quelques minutes.`);
                // Lance le polling pour afficher la barre de progression du job
                if (data.data.job_id && startPolling) startPolling(data.data.job_id, thematiqueId);
                // Reset inputs mais GARDE la modale ouverte → la progression reste visible.
                setMultiDocs([]);
                setLabel('');
                setCategoryId('');
                fetchList(thematiqueId);
                if (onGenerated) onGenerated();
                setTimeout(() => setSuccessMsg(''), 8000);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'Erreur upload');
        }
        setMultiDocSubmitting(false);
    };

    const handleCreateEmpty = async (e) => {
        e?.preventDefault();
        if (!label.trim()) return setError('Donnez un nom a votre sujet');
        setCreatingEmpty(true);
        setError('');
        try {
            await api.post('/custom/create-subject.php', {
                thematique_id: thematiqueId,
                label: label.trim(),
                user_category_id: categoryId || undefined,
            });
            setLabel('');
            setCategoryId('');
            setExpanded(false);
            fetchList(thematiqueId);
            setSuccessMsg('Sujet cr\u00e9\u00e9 ! Ajoutez vos questions ci-dessous.');
            setTimeout(() => setSuccessMsg(''), 6000);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setCreatingEmpty(false);
    };

    const handleGenerateFromPrompt = async (e) => {
        e?.preventDefault();
        // En mode enrichissement, label est r\u00e9cup\u00e9r\u00e9 du parent c\u00f4t\u00e9 backend \u2192 on n'exige
        // pas le label c\u00f4t\u00e9 form, et le batch admin n'a pas de sens (1 enrichissement = 1 ajout).
        const allRows = enrichmentTarget
            ? (aiConsignes.trim().length >= 10 ? [{ label: '', consignes: aiConsignes }] : [])
            : [
                { label: label.trim(), consignes: aiConsignes },
                ...aiBatchExtra.map(r => ({ label: (r.label || '').trim(), consignes: r.consignes || '' })),
            ].filter(r => r.label && r.consignes.trim().length >= 10);

        if (allRows.length === 0) {
            return setError(enrichmentTarget
                ? 'Donne au moins 10 caract\u00e8res de consignes'
                : 'Donne au moins un titre et 10 caract\u00e8res de consignes');
        }

        setAiGenerating(true);
        setError('');
        // Oublie la bannière du job async précédent (ex : un « Resoumettre » terminé) —
        // sinon elle masque le message de succès de CETTE génération (rendu conditionné à !activeJob).
        clearActiveJob();
        setAiBatchProgress({ current: 0, total: allRows.length, success: 0, errors: [] });

        let success = 0;
        const errors = [];
        let totalQuestions = 0;

        for (let i = 0; i < allRows.length; i++) {
            const row = allRows[i];
            setAiBatchProgress({ current: i + 1, total: allRows.length, success, errors: [...errors] });
            try {
                const payload = {
                    thematique_id: thematiqueId,
                    consignes: row.consignes,
                    nb_questions: aiNbQuestions,
                };
                if (enrichmentTarget) {
                    payload.target_parent_ucss_id = enrichmentTarget.id;
                } else {
                    payload.label = row.label;
                    if (categoryId) payload.user_category_id = categoryId;
                }
                const { data } = await api.post('/custom/generate-from-prompt.php', payload);
                success++;
                totalQuestions += data.data?.nb_questions || 0;
            } catch (err) {
                errors.push({ label: row.label || '(enrichissement)', message: err.response?.data?.message || 'Erreur' });
            }
        }

        setAiBatchProgress({ current: allRows.length, total: allRows.length, success, errors });
        setLabel('');
        setAiConsignes('');
        setAiBatchExtra([]);
        setCategoryId('');
        fetchList(thematiqueId);

        const totalMsg = allRows.length === 1
            ? `${totalQuestions} question${totalQuestions > 1 ? 's' : ''} g\u00e9n\u00e9r\u00e9e${totalQuestions > 1 ? 's' : ''} avec succ\u00e8s !`
            : `${success}/${allRows.length} sujet(s) g\u00e9n\u00e9r\u00e9s (${totalQuestions} questions au total)${errors.length ? ` \u2014 ${errors.length} \u00e9chec(s)` : ''}`;
        setSuccessMsg(totalMsg);
        setTimeout(() => setSuccessMsg(''), 10000);

        if (success > 0) {
            // Garde la modale ouverte : le bandeau de génération en haut
            // du form reste visible jusqu'à ce que le user ferme avec la croix.
            if (onGenerated) setTimeout(onGenerated, 1000);
        }

        setAiGenerating(false);
        setTimeout(() => setAiBatchProgress(null), 5000);
    };

    const addBatchRow = () => setAiBatchExtra(prev => [...prev, { label: '', consignes: '' }]);
    const updateBatchRow = (idx, field, value) => setAiBatchExtra(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    const removeBatchRow = (idx) => setAiBatchExtra(prev => prev.filter((_, i) => i !== idx));

    const handleJsonFileSelect = (filesOrSingle) => {
        const files = Array.isArray(filesOrSingle) ? filesOrSingle : [filesOrSingle].filter(Boolean);
        if (files.length === 0) return;

        const rejected = [];
        const valid = [];
        for (const f of files) {
            if (!f.name.toLowerCase().endsWith('.json')) {
                rejected.push(`${f.name} : pas un .json`);
                continue;
            }
            if (f.size > 5 * 1024 * 1024) {
                rejected.push(`${f.name} : > 5 Mo`);
                continue;
            }
            valid.push(f);
        }

        if (rejected.length > 0) setError(rejected.join(', '));
        else setError('');

        // Parse chaque fichier en parallèle, puis enrichir jsonItems
        Promise.all(valid.map(f => new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const data = JSON.parse(ev.target.result);
                    if (data.format !== 'monquiz-import') {
                        resolve({ file: f, error: 'Format non reconnu : "format" doit valoir "monquiz-import"' });
                        return;
                    }
                    const flat = [];
                    (data.categories || []).forEach(cat => {
                        (cat.sous_sujets || []).forEach(ss => flat.push(ss));
                    });
                    if (flat.length === 0) {
                        resolve({ file: f, error: 'Aucun sous-sujet trouvé' });
                        return;
                    }
                    const preview = {
                        nbSujets: flat.length,
                        nbQuestions: flat.reduce((acc, ss) => acc + (ss.questions?.length || 0), 0),
                        sujets: flat.map(ss => ({ label: ss.label, nbQuestions: ss.questions?.length || 0 })),
                        sourceTitle: data.source_title || '',
                        sourceUrl: data.source_url || '',
                    };
                    resolve({ file: f, data, preview });
                } catch (err) {
                    resolve({ file: f, error: 'JSON invalide : ' + err.message });
                }
            };
            reader.readAsText(f);
        }))).then(items => {
            const errors = items.filter(i => i.error);
            const oks = items.filter(i => !i.error);
            if (errors.length > 0) {
                const errMsg = errors.map(e => `${e.file.name} : ${e.error}`).join(' | ');
                setError(prev => prev ? `${prev} | ${errMsg}` : errMsg);
            }
            if (oks.length > 0) {
                setJsonItems(prev => [...prev, ...oks]);
            }
        });
    };

    const removeJsonItem = (idx) => setJsonItems(prev => prev.filter((_, i) => i !== idx));
    const clearJsonItems = () => { setJsonItems([]); setJsonImportProgress(null); };

    const handleImportJson = async (e) => {
        e?.preventDefault();
        if (!isAdmin) return setError('Import JSON réservé aux administrateurs');
        if (jsonItems.length === 0) return setError('Sélectionne au moins un fichier JSON');
        setJsonImporting(true);
        setError('');
        setJsonImportProgress({ current: 0, total: jsonItems.length, results: [] });

        const results = [];
        let totalSujets = 0;
        let totalQuestions = 0;
        let okCount = 0;

        for (let i = 0; i < jsonItems.length; i++) {
            const item = jsonItems[i];
            setJsonImportProgress({ current: i + 1, total: jsonItems.length, results: [...results] });
            try {
                const { data } = await api.post('/custom/import-json.php', {
                    thematique_id: thematiqueId,
                    user_category_id: categoryId || undefined,
                    source_label: item.file?.name || '',
                    source_reference: item.preview.sourceUrl || undefined,
                    payload: item.data,
                });
                const nbS = data.data?.nb_sujets || 0;
                const nbQ = data.data?.nb_questions || 0;
                results.push({ filename: item.file.name, ok: true, nbS, nbQ });
                totalSujets += nbS;
                totalQuestions += nbQ;
                okCount++;
            } catch (err) {
                results.push({
                    filename: item.file.name,
                    ok: false,
                    error: err.response?.data?.message || 'Erreur d\'import',
                });
            }
        }

        setJsonImportProgress({ current: jsonItems.length, total: jsonItems.length, results });

        if (okCount === jsonItems.length) {
            // Tout est OK — on ferme le formulaire
            setJsonItems([]);
            setCategoryId('');
            setExpanded(false);
        }
        fetchList(thematiqueId);

        const failedCount = jsonItems.length - okCount;
        const msg = jsonItems.length === 1
            ? `${totalSujets} sujet${totalSujets > 1 ? 's' : ''} importé${totalSujets > 1 ? 's' : ''} (${totalQuestions} questions).`
            : `${okCount}/${jsonItems.length} fichier(s) importé(s) — ${totalSujets} sujets, ${totalQuestions} questions${failedCount > 0 ? ` (${failedCount} échec${failedCount > 1 ? 's' : ''})` : ''}.`;
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 10000);
        if (okCount > 0 && onGenerated) setTimeout(onGenerated, 1000);

        setJsonImporting(false);
        // Garde le progress affiché un moment puis efface
        setTimeout(() => setJsonImportProgress(null), 8000);
    };

    const fileRef = useRef(null);
    const dropRef = useRef(null);

    useEffect(() => {
        if (thematiqueId) fetchList(thematiqueId);
        return () => stopPolling();
    }, [thematiqueId]);

    // Persiste expanded + activeTab par thematique
    useEffect(() => {
        if (typeof window === 'undefined' || !thematiqueId) return;
        try {
            window.localStorage.setItem(
                `mt-upload-state-${thematiqueId}`,
                JSON.stringify({ expanded, activeTab }),
            );
        } catch { /* quota / private mode : ignore */ }
    }, [thematiqueId, expanded, activeTab]);

    // Cleanup blob URL on unmount or file change
    useEffect(() => {
        return () => { if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl); };
    }, [filePreviewUrl]);

    // Set file + auto-label + create preview URL
    const selectFile = useCallback((f) => {
        if (!f) return;
        const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/csv', 'text/html', 'image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(f.type)) {
            setError('Format non supporté. Formats acceptés : PDF, DOCX, TXT, CSV, images');
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            setError('Le fichier dépasse 10 Mo');
            return;
        }
        // Revoke previous blob URL
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
        const blobUrl = URL.createObjectURL(f);
        setFile(f);
        setFilePreviewUrl(blobUrl);
        setError('');
        if (!label.trim()) {
            setLabel(labelFromFilename(f.name));
        }
        if (!expanded) setExpanded(true);
    }, [label, expanded, filePreviewUrl]);

    // Open preview for a local file (before upload)
    const openLocalPreview = () => {
        if (filePreviewUrl) {
            setPreviewSrc(filePreviewUrl);
            setPreviewTitle(file?.name || 'Aperçu');
            setPreviewOpen(true);
        }
    };

    // Drag & drop handlers
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only leave if exiting the drop zone
        if (dropRef.current && !dropRef.current.contains(e.relatedTarget)) {
            setDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            selectFile(files[0]);
        }
    }, [selectFile]);

    const [canRetry, setCanRetry] = useState(false);

    const handleSubmit = async (e, force = false) => {
        e?.preventDefault();
        setError('');
        setCanRetry(false);
        // En mode enrichissement, label/category sont récupérés du parent côté backend
        if (!enrichmentTarget && !label.trim()) return setError('Donnez un nom à votre sujet');
        if (!file) return setError('Sélectionnez un document');

        try {
            await uploadPdf(
                file,
                enrichmentTarget ? '' : label.trim(),
                thematiqueId,
                '',
                enrichmentTarget ? null : (categoryId || null),
                force,
                enrichmentTarget ? enrichmentTarget.id : null,
                aiNbQuestions,
            );
            // Reset inputs mais GARDE la modale ouverte → le bandeau de progression
            // (rendu en haut du form) reste visible pendant la génération IA.
            setLabel('');
            setFile(null);
            setCategoryId('');
            if (fileRef.current) fileRef.current.value = '';
            if (onGenerated) setTimeout(onGenerated, 3000);
        } catch (err) {
            setError(err.message);
            if (err.canRetry) setCanRetry(true);
        }
    };

    const isGenerating = activeJob && (activeJob.status === 'QUEUED' || activeJob.status === 'PROCESSING');

    const content = (
        <div
            className={`mt-upload-module ${dragging ? 'mt-upload-module--dragging' : ''}`}
            ref={dropRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Drag overlay */}
            {dragging && (
                <div className="mt-upload-drop-overlay">
                    <Illustration svg={illuTeleverser} color="#6d28d9" className="mt-upload-illu mt-upload-illu--overlay" />
                    <span>Déposez votre document ici</span>
                </div>
            )}

            {/* Échec de génération → proposition de resoumettre direct (1er échec) */}
            <ConfirmDialog
                open={!!failedJob}
                title="Génération momentanément indisponible"
                message={<>
                    La génération n'a pas abouti — le service est peut-être saturé.<br />
                    Vous pouvez <strong>resoumettre</strong> la demande maintenant. Si l'erreur persiste,
                    écrivez-nous à <strong>contact@monquiz.fr</strong>.
                </>}
                confirmLabel="Resoumettre"
                cancelLabel="Fermer"
                onConfirm={async () => {
                    const job = failedJob;
                    setFailedJob(null);
                    if (job?.custom_ss_id) {
                        // Un nouvel échec re-déclenchera ce popup via le polling (nouveau job_id).
                        try { await resubmitJob(job.custom_ss_id, thematiqueId); } catch { /* surfacé par le polling */ }
                    }
                }}
                onCancel={() => setFailedJob(null)}
            />

            {/* Generation progress — affiché EN-DEHORS de la modale uniquement,
                pour rester visible si le user a fermé la modale.
                Quand expanded === true, le bandeau est rendu DANS le form (en haut). */}
            {!expanded && isGenerating && (
                <div className="mt-upload-progress">
                    <Loader2 size={16} className="spin" />
                    <span>Génération en cours... {activeJob.progress}%</span>
                    <div className="mt-upload-progress__bar">
                        <div style={{ width: `${activeJob.progress}%` }} />
                    </div>
                    <span className="mt-upload-progress__hint">Cela peut prendre quelques minutes : la génération continue même si vous quittez la page, le sujet apparaîtra dans la liste une fois prêt.</span>
                </div>
            )}

            {!expanded && activeJob?.status === 'COMPLETED' && (
                <div className="mt-upload-success">
                    <CheckCircle size={16} />
                    <span>{activeJob.nb_questions_generated} questions générées en {activeJob.generation_time}s</span>
                    {onOpenSujet && activeJob.sous_sujet_id > 0 && (
                        <button type="button" className="mt-upload-success__link" onClick={() => onOpenSujet(activeJob.sous_sujet_id)}>
                            Voir le sujet <ArrowRight size={14} />
                        </button>
                    )}
                </div>
            )}

            {!expanded && successMsg && (
                <div className="mt-upload-success">
                    <CheckCircle size={16} />
                    <span>{successMsg}</span>
                </div>
            )}

            {/* Contextual pack upsell — visible at >=80% of any quota or when exceeded */}
            <PackBoosterCTA
                thematiqueId={thematiqueId}
                sujetQuota={quota ? { used: quota.used_sous_sujets, max: quota.max_sous_sujets } : null}
                onPurchased={() => aiQuota.reload()}
            />

            {/* Collapsed / Expanded toggle */}
            {!expanded ? (
                <button
                    className="mt-upload-trigger"
                    onClick={() => setExpanded(true)}
                    disabled={isGenerating}
                >
                    <Settings size={16} />
                    Gestion des sujets
                    {quota && !quota.unlimited && (
                        <span className="mt-upload-quota" title="Chaque création ou enrichissement consomme 1 génération IA">
                            {quota.used_ai_generations ?? quota.used_sous_sujets}/{quota.max_ai_generations ?? quota.max_sous_sujets} générations IA
                        </span>
                    )}
                </button>
            ) : (
                <div className="mt-upload-form">
                    {/* Tabs nav (shared between Add et Historique) */}
                    <div className="ms-head">
                        <div className="ms-tabs">
                            <button type="button" className={`ms-tab ${activeTab === 'add' ? 'is-active' : ''}`} onClick={() => setActiveTab('add')}>
                                <Illustration svg={illuAjouterContenu} color="#6d28d9" className="ms-tab__illu" /> {enrichmentTarget ? 'Compléter un sujet' : 'Ajouter du contenu'}
                            </button>
                            <button type="button" className={`ms-tab ${activeTab === 'history' ? 'is-active' : ''}`} onClick={() => setActiveTab('history')}>
                                <Illustration svg={illuHistorique} color="#6d28d9" className="ms-tab__illu" /> Historique ({sousSujets.length})
                            </button>
                            <button type="button" className={`ms-tab ${activeTab === 'codes' ? 'is-active' : ''}`} onClick={() => setActiveTab('codes')}>
                                <Illustration svg={illuGestionCodes} color="#6d28d9" className="ms-tab__illu" /> Gestion par code
                            </button>
                        </div>
                        {quota && !quota.unlimited && activeTab === 'add' && (
                            <span className="mt-upload-form__quota" style={{ marginLeft: 'auto' }} title="Chaque création ou enrichissement consomme 1 génération IA">
                                {quota.used_ai_generations ?? quota.used_sous_sujets}/{quota.max_ai_generations ?? quota.max_sous_sujets} générations IA
                            </span>
                        )}
                        {/* ✕ = repli du form (setExpanded). Inutile en modale (forceExpanded → toujours déplié) : masqué. */}
                        {!forceExpanded && (
                            <button type="button" className="mt-upload-close" style={{ marginLeft: 'auto' }}
                                onClick={() => { setExpanded(false); setFile(null); setLabel(''); setError(''); }}>
                                &times;
                            </button>
                        )}
                    </div>

                    {activeTab === 'add' && (
                    <form
                        style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                        onSubmit={
                            createMode === 'document' ? handleSubmit
                            : createMode === 'empty' ? handleCreateEmpty
                            : createMode === 'ai-prompt' ? handleGenerateFromPrompt
                            : createMode === 'multi-doc' ? handleSubmitMultiDoc
                            : handleImportJson
                        }
                    >

                    {/* Toggle "Nouveau sujet" / "Compléter un sujet existant" + rappel enrichissement.
                        Toggle masqué si le parent force enrichmentTargetProp. */}
                    <EnrichmentSwitch
                        sousSujets={sousSujets}
                        enrichmentTarget={enrichmentTarget}
                        setEnrichmentTarget={setEnrichmentTarget}
                        clearEnrichmentTarget={clearEnrichmentTarget}
                        showToggle={!enrichmentTargetProp}
                    />

                    {/* Bandeau de génération en cours / terminée — affiché DANS la modale
                        pour rester visible même si le user scrolle la liste des sujets en arrière-plan. */}
                    {isGenerating && (
                        <div className="mt-upload-progress" style={{ marginBottom: 10 }}>
                            <Loader2 size={16} className="spin" />
                            <span>Génération en cours… {activeJob.progress}%</span>
                            <div className="mt-upload-progress__bar">
                                <div style={{ width: `${activeJob.progress}%` }} />
                            </div>
                            <span className="mt-upload-progress__hint">Cela peut prendre quelques minutes. Vous pouvez fermer cette fenêtre : la génération continue et le sujet apparaîtra une fois prêt.</span>
                        </div>
                    )}
                    {activeJob?.status === 'COMPLETED' && (
                        <div className="mt-upload-success" style={{ marginBottom: 10 }}>
                            <CheckCircle size={16} />
                            <span>{activeJob.nb_questions_generated} questions générées en {activeJob.generation_time}s — vous pouvez fermer cette fenêtre ou créer un autre sujet.</span>
                            {onOpenSujet && activeJob.sous_sujet_id > 0 && (
                                <button type="button" className="mt-upload-success__link" onClick={() => onOpenSujet(activeJob.sous_sujet_id)}>
                                    Voir le sujet <ArrowRight size={14} />
                                </button>
                            )}
                        </div>
                    )}
                    {successMsg && !activeJob && (
                        <div className="mt-upload-success" style={{ marginBottom: 10 }}>
                            <CheckCircle size={16} />
                            <span>{successMsg}</span>
                        </div>
                    )}

                    {/* Historique : 5 dernières générations (statut visible) */}
                    <RecentGenerations sousSujets={sousSujets} open={recentOpen} onToggle={() => setRecentOpen(o => !o)} />

                    {/* Source du contenu — cartes + encart descriptif (crédits IA partagés doc/prompt/multi-doc) */}
                    <SourceSelector
                        createMode={createMode} setCreateMode={setCreateMode}
                        enrichmentTarget={enrichmentTarget} isAdmin={isAdmin}
                        quota={quota} aiQuota={aiQuota} thematiqueId={thematiqueId}
                    />

                    {/* === MODE: Document upload === */}
                    {createMode === 'document' && (
                        <DocumentModeFields
                            file={file} fileRef={fileRef} selectFile={selectFile}
                            onPreview={openLocalPreview}
                            onClearFile={() => { setFile(null); setFilePreviewUrl(null); if (fileRef.current) fileRef.current.value = ''; }}
                            uploading={uploading}
                            enrichmentTarget={enrichmentTarget} label={label} setLabel={setLabel}
                            aiNbQuestions={aiNbQuestions} setAiNbQuestions={setAiNbQuestions}
                            categories={categories} categoryId={categoryId} setCategoryId={setCategoryId}
                        />
                    )}

                    {/* === MODE: Multi-doc compilation === */}
                    {createMode === 'multi-doc' && <>
                        <MultiDocPicker
                            enrichmentTarget={enrichmentTarget} label={label} setLabel={setLabel}
                            submitting={multiDocSubmitting}
                            aiNbQuestions={aiNbQuestions} setAiNbQuestions={setAiNbQuestions}
                            dragging={dragging} setDragging={setDragging}
                            fileRef={multiDocFileRef} onAddFiles={handleAddMultiDocFiles} maxDocs={MAX_DOCS}
                        />

                        {/* Liste des docs ajoutés + barre de capacité tokens */}
                        <MultiDocList docs={multiDocs} totalTokens={totalMultiTokens} maxTokens={MAX_TOKENS_TOTAL} onRemove={removeMultiDoc} />

                    </>}

                    {/* === MODE: Empty subject === */}
                    {createMode === 'empty' && <>
                        <input type="text" placeholder="Nom du sujet" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} disabled={creatingEmpty} />
                    </>}

                    {/* === MODE: AI prompt === */}
                    {createMode === 'ai-prompt' && <>
                        {!enrichmentTarget && (
                            <PromptIdeas disabled={aiGenerating} defaultOpen={!aiConsignes.trim()}
                                onUseIdea={({ label: l, consignes }) => { setLabel(l); setAiConsignes(consignes); }} />
                        )}
                        {!enrichmentTarget && (
                            <input type="text" placeholder="Titre du sujet (ex : « Quiz fun sur les années 90 »)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} disabled={aiGenerating} />
                        )}
                        <textarea
                            placeholder={"Consignes pour l'IA — soyez explicite sur le thème, le public et le ton. Exemples :\n• « 10 questions sur la Ve République pour un concours d'adjoint administratif, niveau intermédiaire »\n• « Culture générale années 90 : musique, ciné, pub TV, 30 questions grand public »\n• « Quiz sur les capitales du monde, niveau débutant, ton pédagogique »"}
                            value={aiConsignes}
                            onChange={(e) => setAiConsignes(e.target.value)}
                            disabled={aiGenerating}
                            rows={6}
                            style={{ resize: 'vertical', minHeight: 110 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>Nombre approximatif de questions :</label>
                            <select value={aiNbQuestions} onChange={e => setAiNbQuestions(Number(e.target.value))} disabled={aiGenerating}
                                className="mt-upload-category-select" style={{ width: 'auto' }}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                                <option value={50}>50</option>
                            </select>
                            <span style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>(par sujet)</span>
                        </div>

                        {/* Sujets additionnels (admin only) + progression batch — désactivé en enrichissement (1 batch = 1 ajout au parent) */}
                        {isAdmin && !enrichmentTarget && (
                            <AiBatchSection
                                rows={aiBatchExtra} onAdd={addBatchRow} onUpdate={updateBatchRow} onRemove={removeBatchRow}
                                progress={aiBatchProgress} generating={aiGenerating}
                            />
                        )}
                    </>}

                    {/* === MODE: JSON import (multi-fichiers) === */}
                    {createMode === 'json-import' && <>
                        <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                            Importe un ou plusieurs fichiers JSON au format <code>monquiz-import</code>. Tous les sous-sujets et questions seront créés dans cette thématique.
                        </p>
                        <div
                            className="mt-upload-dropzone"
                            onClick={() => !jsonImporting && document.getElementById('mt-json-input')?.click()}
                        >
                            <div className="mt-upload-dropzone__empty">
                                <Illustration svg={illuTeleverser} color="#6d28d9" className="mt-upload-illu" />
                                <span>Clique pour choisir un ou plusieurs fichiers <strong>.json</strong></span>
                                <small>Format <code>monquiz-import v1.0</code> — Max 5 Mo par fichier</small>
                            </div>
                            <input
                                id="mt-json-input"
                                type="file"
                                accept=".json,application/json"
                                multiple
                                onChange={(e) => { handleJsonFileSelect(Array.from(e.target.files || [])); e.target.value = ''; }}
                                disabled={jsonImporting}
                                hidden
                            />
                        </div>

                        {/* Liste des fichiers sélectionnés (preview) + progression/résultats par fichier */}
                        <JsonItemsSection
                            items={jsonItems} importing={jsonImporting} progress={jsonImportProgress}
                            onRemove={removeJsonItem} onClear={clearJsonItems}
                        />
                    </>}

                    {/* Category selector (autres modes ; pour 'document' il est dans la config 2 colonnes). */}
                    {categories.length > 0 && !enrichmentTarget && createMode !== 'document' && (
                        <select
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                            disabled={uploading || creatingEmpty || aiGenerating || jsonImporting || multiDocSubmitting}
                            className="mt-upload-category-select"
                        >
                            <option value="">Sans cat&eacute;gorie</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </select>
                    )}

                    <div className="mt-upload-form__row">
                        {createMode === 'document' && (
                            <span className="mt-upload-estimate">10 &agrave; 50 questions par QCM</span>
                        )}
                        {createMode === 'empty' && (
                            <span className="mt-upload-estimate">Vous ajouterez les questions ensuite</span>
                        )}
                        {createMode === 'ai-prompt' && (
                            <span className="mt-upload-estimate">{aiNbQuestions} questions seront g&eacute;n&eacute;r&eacute;es</span>
                        )}
                        {createMode === 'json-import' && (
                            <span className="mt-upload-estimate">
                                {jsonItems.length > 0
                                    ? `${jsonItems.length} fichier${jsonItems.length > 1 ? 's' : ''} \u00b7 ${jsonItems.reduce((acc, i) => acc + i.preview.nbSujets, 0)} sujet(s), ${jsonItems.reduce((acc, i) => acc + i.preview.nbQuestions, 0)} questions`
                                    : 'Aucun fichier charg\u00e9'}
                            </span>
                        )}

                        {['document', 'multi-doc', 'ai-prompt'].includes(createMode) && <AiContentNotice />}
                        {createMode === 'document' && (
                            <button type="submit" className="btn btn-primary mt-upload-submit"
                                disabled={uploading || (!enrichmentTarget && !label.trim()) || !file}>
                                {uploading ? <><Loader2 size={14} className="spin" /> Upload...</> : <><Sparkles size={14} /> G&eacute;n&eacute;rer mon Quiz</>}
                            </button>
                        )}
                        {createMode === 'multi-doc' && (() => {
                            const readyCount = multiDocs.filter(d => !d.error && d.text).length;
                            const reasons = [];
                            if (!enrichmentTarget && !label.trim()) reasons.push('renseignez le nom du sujet');
                            if (multiDocs.length === 0) reasons.push('ajoutez au moins un document');
                            if (multiDocs.some(d => d.extracting)) reasons.push('attendez la fin de l’extraction');
                            if (totalMultiTokens > MAX_TOKENS_TOTAL) reasons.push('retirez des documents (capacité dépassée)');
                            if (multiDocs.length > 0 && readyCount === 0) reasons.push('tous les documents ont échoué à l’extraction');
                            const isDisabled = multiDocSubmitting || reasons.length > 0;
                            return (
                                <>
                                    <button type="submit" className="btn btn-primary mt-upload-submit"
                                        disabled={isDisabled}
                                        title={isDisabled && reasons.length > 0 ? reasons.join(' · ') : ''}>
                                        {multiDocSubmitting
                                            ? <><Loader2 size={14} className="spin" /> Lancement...</>
                                            : <><Layers size={14} /> Compiler {readyCount || ''} docs et g&eacute;n&eacute;rer</>}
                                    </button>
                                    {!multiDocSubmitting && reasons.length > 0 && (
                                        <span style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginLeft: 8, alignSelf: 'center' }}>
                                            Pour activer : {reasons.join(' · ')}
                                        </span>
                                    )}
                                </>
                            );
                        })()}
                        {createMode === 'empty' && (
                            <button type="submit" className="btn btn-primary mt-upload-submit" disabled={creatingEmpty || !label.trim()}>
                                {creatingEmpty ? <><Loader2 size={14} className="spin" /> Cr&eacute;ation...</> : <><PenLine size={14} /> Cr&eacute;er le sujet</>}
                            </button>
                        )}
                        {createMode === 'ai-prompt' && (() => {
                            const batchTotal = 1 + aiBatchExtra.filter(r => (r.label || '').trim() && (r.consignes || '').trim().length >= 10).length;
                            const isBatch = batchTotal > 1;
                            const buttonLabel = isBatch
                                ? `Générer ${batchTotal} sujets (~${batchTotal * aiNbQuestions} questions)`
                                : `Générer ${aiNbQuestions} questions`;
                            return (
                                <>
                                <button type="submit" className="btn btn-primary mt-upload-submit"
                                    disabled={aiGenerating || (!enrichmentTarget && !label.trim()) || aiConsignes.length < 10}>
                                    {aiGenerating
                                        ? <><Loader2 size={14} className="spin" /> {aiBatchProgress && aiBatchProgress.total > 1 ? `Génération ${aiBatchProgress.current}/${aiBatchProgress.total}…` : 'Génération en cours...'}</>
                                        : <><Brain size={14} /> {buttonLabel}</>}
                                </button>
                                {aiGenerating && (
                                    <p className="mt-upload-wait">Cela peut prendre quelques minutes : merci de garder cette page ouverte.</p>
                                )}
                                </>
                            );
                        })()}
                        {createMode === 'json-import' && (() => {
                            const totalSujets = jsonItems.reduce((acc, i) => acc + i.preview.nbSujets, 0);
                            return (
                                <button type="submit" className="btn btn-primary mt-upload-submit" disabled={jsonImporting || jsonItems.length === 0}>
                                    {jsonImporting
                                        ? <><Loader2 size={14} className="spin" /> {jsonImportProgress && jsonImportProgress.total > 1 ? `Import ${jsonImportProgress.current}/${jsonImportProgress.total}…` : 'Import...'}</>
                                        : <><FileJson size={14} /> Importer{totalSujets > 0 ? ` ${totalSujets} sujet${totalSujets > 1 ? 's' : ''}` : ''}{jsonItems.length > 1 ? ` (${jsonItems.length} fichiers)` : ''}</>}
                                </button>
                            );
                        })()}
                    </div>

                    {error && (
                        <div className="mt-upload-error">
                            <AlertTriangle size={14} /> {error}
                            {canRetry && (
                                <button className="btn btn-xs btn-primary" style={{ marginLeft: '8px' }}
                                    onClick={(e) => handleSubmit(e, true)}>
                                    Resoumettre
                                </button>
                            )}
                        </div>
                    )}
                    </form>
                    )}

                    {activeTab === 'codes' && <CodesTab thematiqueId={thematiqueId} />}

                    {activeTab === 'history' && (
                        <div style={{ marginTop: 12 }}>
                            <MaThematiqueGenerations thematiqueId={thematiqueId} />
                        </div>
                    )}
                </div>
            )}

            {/* PDF Preview Modal */}
            {previewOpen && previewSrc && (
                <PdfPreviewModal
                    src={previewSrc}
                    title={previewTitle}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </div>
    );

    // Essai gratuit (migration 181) : sujet pas encore créé → écran d'essai simplifié ;
    // sinon toutes les sources restent visibles, grisées, avec l'offre d'abonnement.
    if (!trial) return content;
    // can_generate : essai ouvert et sujet généré pas encore créé (un sujet d'exemple ne compte pas).
    if (trial.can_generate && !isGenerating) {
        return (
            <TrialStartCard
                framed={false}
                thematiqueId={thematiqueId}
                nbQuestions={trial.nb_questions || 20}
                days={trial.days || 7}
                onStarted={({ jobId, sousSujetId }) => {
                    if (jobId) startPolling(jobId, thematiqueId);
                    fetchList(thematiqueId);
                    if (onGenerated) setTimeout(onGenerated, jobId ? 1500 : 0);
                    if (!jobId && sousSujetId && onOpenSujet) setTimeout(() => onOpenSujet(sousSujetId), 600);
                }}
            />
        );
    }
    const examplesPicker = trial.state !== 'expired' && (
        <TrialExamplesPicker title="Bibliothèque de sujets d’exemple (relus, ajout gratuit)"
            onAdded={({ sousSujetId }) => {
                fetchList(thematiqueId);
                if (onGenerated) onGenerated();
                if (sousSujetId && onOpenSujet) setTimeout(() => onOpenSujet(sousSujetId), 600);
            }} />
    );
    return (
        <>
            {examplesPicker && <div className="trial-ex-library">{examplesPicker}</div>}
            <div className="trial-lock-note">
                <Lock size={16} />
                <span>
                    {trial.state === 'expired'
                        ? "L'essai gratuit est terminé. "
                        : `L'essai gratuit comprend un sujet de ${trial.nb_questions || 20} questions. `}
                    Avec l'abonnement : d'autres sujets (document, compilation, idée, sujet vide, import), l'enrichissement de vos sujets et les catégories.
                </span>
                {onUnlock && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={onUnlock}>Débloquer la thématique</button>
                )}
            </div>
            <div className="trial-locked" aria-disabled="true">{content}</div>
        </>
    );
}
